import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { pushAPI } from '../lib/api';

export function usePushNotifications() {
  const { isAuthenticated } = useAuth();
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !('serviceWorker' in navigator)) return;
    navigator.serviceWorker.ready.then(registration => {
      registration.pushManager.getSubscription().then(sub => {
        setIsSubscribed(!!sub);
      });
    }).catch(() => {});
  }, [isAuthenticated]);

  const subscribe = useCallback(async () => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      return { success: false, reason: 'not_supported' };
    }

    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);

      if (perm !== 'granted') {
        return { success: false, reason: 'denied' };
      }

      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        // Use VAPID key placeholder - in production, replace with actual key
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: 'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkCs7q2_Bh3BVFIw0x20-cPYYYfBq8bS2_cPrqjJLA'
        });
      }

      await pushAPI.subscribe(subscription.toJSON());
      setIsSubscribed(true);
      return { success: true };
    } catch (error) {
      console.error('Push subscribe error:', error);
      return { success: false, reason: 'error' };
    } finally {
      setLoading(false);
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }
      await pushAPI.unsubscribe();
      setIsSubscribed(false);
      return { success: true };
    } catch (error) {
      console.error('Push unsubscribe error:', error);
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    permission,
    isSubscribed,
    loading,
    isSupported: 'Notification' in window && 'serviceWorker' in navigator,
    subscribe,
    unsubscribe,
  };
}
